import React from 'react';
import PropTypes from 'prop-types';
import Table from 'src/ui/library/Table';

/*
 * This component is an extension of the Table component. It allows you to quickly create
 * a table that will manage adding/removing of rows
 *
 */

class TableAddRemove extends React.Component {
  static propTypes = {
    /** Classname to be applied to <TableAddRemove> */
    className: PropTypes.string,
    /** Styles to be applied to component */
    classes: PropTypes.object,
    /** Data that will be used to generate table columns */
    columns: PropTypes.PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayText: PropTypes.string.isRequired,
        visible: PropTypes.bool,
        sticky: PropTypes.bool,
        sortable: PropTypes.bool,
        renderComponent: PropTypes.function, // allows for custom cell rendering
      }),
    ).isRequired,
    /** Data that will be used to generate table rows */
    rows: PropTypes.array.isRequired,
    /** Label to be used for the add row link */
    addRowLabel: PropTypes.string,
    /** Label to be used for the remove row link */
    removeRowLabel: PropTypes.string,
    /**
     * Read Only mode will force table to ignore renderComponent per columns and display cell value.
     * It will also make rows not selectable.
     */
    readOnly: PropTypes.bool,
    /** Hide header */
    hideHeader: PropTypes.bool,
    /** Page for the table to open at */
    page: PropTypes.number,
    /** Enable/disable table pagination */
    pagination: PropTypes.bool,
    /** If provided, the total number of rows for the table */
    totalRows: PropTypes.number,
    /** If provided, the total number of rows per page */
    rowsPerPage: PropTypes.number,
    /** Server side mode means that the Table component will not handle pagination/sort. Useful for depedency on API's */
    serverSide: PropTypes.bool,
    /** Populate first column with selectable radio button */
    selectableRows: PropTypes.bool,
    /** Provide custom footer to display with the Table */
    footer: PropTypes.func,
    /** Callback function to get reference to internal setTableModel() method */
    setTableModel: PropTypes.func,
    /** Callback function to get reference to internal updateColumnValue() method */
    updateColumn: PropTypes.func,
    /** Callback function to get reference to internal cancelSelectedRows() method */
    cancelSelectedRows: PropTypes.func,
    /** Callback function to get reference to internal updateModelValue() method */
    updateModel: PropTypes.func,
    /**
     * Callback function for table updates. fn(actionType: string, tableState: object)
     * tableState enum: ('rowClick', 'cellClick', 'pageChange', 'colSort', 'rowsSelected', 'rowsDeleted', 'rowAdded')
     */
    onTableChange: PropTypes.func,
    // Message for when no table data available
    noTableDataMsg: PropTypes.string,
  };

  static defaultProps = {
    addRowLabel: null,
    removeRowLabel: null,
  };

  state = {
    showDelete: false,
    rowsSelected: [],
  };

  constructor(props) {
    super(props);
    // callback to access cancelSelectRows method inside Table
    this.cancelSelectedRowsFn = null;
  }

  handleDeleteRows = rows => {
    const rowsToRemove = rows.map(row => row.origRowIndex);
    const newRows = this.props.rows.filter((_, index) => !rowsToRemove.includes(index));
    this.props.onTableChange('rowsDeleted', { rowsDeleted: rows, newRows });
    this.cancelSelectedRowsFn();
  };

  handleAddRow = () => {
    // simply create an empty row and put it at position 0
    const numOfColumns = this.props.columns.length;
    const emptyRow = Array.from({ length: numOfColumns }).map(col => '');
    const newRows = [emptyRow, ...this.props.rows];
    this.props.onTableChange('rowAdded', { newRows });
  };

  onTableChange = (actionType, data) => {
    switch (actionType) {
      case 'rowSelected':
        const { selectedRows } = data.model;

        this.setState({
          showDelete: selectedRows.length ? true : false,
          rowsSelected: selectedRows,
        });

        break;
      default:
        break;
    }

    if (this.props.onTableChange) {
      this.props.onTableChange(actionType, data);
    }
  };

  render() {
    const {
      readOnly,
      addRowLabel,
      removeRowLabel,
      setUpdateModelFn,
      cancelRowsFn,
      setUpdateColumnFn,
      onTableChange,
      ...otherProps
    } = this.props;

    return (
      <div className={'table-addremove'}>
        <section className="table-actions">
          {!readOnly &&
            (this.state.showDelete ? (
              <a onClick={this.handleDeleteRows.bind(null, this.state.rowsSelected)}>{removeRowLabel || 'Remove'}</a>
            ) : (
              <a onClick={this.handleAddRow}>{addRowLabel || '+ Add Team Member'}</a>
            ))}
        </section>
        <Table
          readOnly={readOnly}
          hideHeader={true}
          selectableRows={true}
          onTableChange={this.onTableChange}
          cancelSelectedRows={fn => (this.cancelSelectedRowsFn = fn)}
          {...otherProps}
        />
      </div>
    );
  }
}

export default TableAddRemove;
